import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Form, Input, Button, Space, message, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const ProfileSettings = () => {
  const [form] = Form.useForm();
  const { currentUser, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Load current user details
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!currentUser?.id) { setLoading(false); return; }
      try {
        const data = await api.get(`/users/${currentUser.id}`);
        if (!alive) return;
        const init = {
          id: data.id,
          full_name: data.full_name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          email: data.email || '',
          password_hash: data.password_hash || ''
        };
        form.setFieldsValue(init);
        setAvatarUrl(data.profile_picture_url || null);
      } catch (e) {
        console.error(e);
        message.error('Failed to load profile');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [currentUser, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        full_name: values.full_name,
        email: values.email,
        password_hash: values.password_hash
      };
      await api.put(`/users/${currentUser.id}`, payload);
      updateUser(payload);
      message.success('Profile saved');
    } catch (e) {
      if (e?.errorFields) return; // antd validation
      console.error(e);
      message.error(e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  // Helpers
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadProps = {
    name: 'file',
    accept: 'image/*',
    showUploadList: false,
    maxCount: 1,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        if (!file || !(file).type?.startsWith('image/')) {
          message.error('Please select a valid image file');
          return onError?.(new Error('Invalid file'));
        }
        setUploading(true);
        const dataUrl = await fileToDataUrl(file);
        const updated = await api.uploadUserProfilePicture(currentUser.id, { dataUrl });
        setAvatarUrl(updated.profile_picture_url || null);
        updateUser({ profile_picture_url: updated.profile_picture_url || null });
        message.success('Profile picture updated');
        onSuccess?.(updated);
      } catch (err) {
        console.error(err);
        message.error(err.message || 'Failed to upload picture');
        onError?.(err);
      } finally {
        setUploading(false);
      }
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Settings' }, { title: 'Profile' }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Profile Settings</Title>
        <Space>
          <Button type="primary" onClick={onSave} loading={saving}>Save</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary">View and update your account information.</Paragraph>
        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, marginBottom: 12 }}>
          <img
            src={avatarUrl || '/icon.png'}
            alt="avatar"
            style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '1px solid #f0f0f0' }}
            onError={(e) => { e.currentTarget.src = '/icon.png'; }}
          />
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={uploading}>Upload new picture</Button>
          </Upload>
        </div>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} disabled={loading}>
          <Form.Item label="ID" name="id">
            <Input disabled />
          </Form.Item>
          <Form.Item label="Full Name" name="full_name" rules={[{ required: true, message: 'Full name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ type: 'email', required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Password Hash" name="password_hash">
            <Input.Password placeholder="Update only if you know what you're doing" />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ProfileSettings;
